import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/LeadTable";
import { toast } from "sonner";
import { ClipboardList, FileDown, FileText, Loader2, Search } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export default function ManagerReporting() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  const params = useCallback(() => Object.fromEntries(Object.entries({ date_from: from, date_to: to }).filter(([, v]) => v)), [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/manager-remarks", { params: params() });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (type) => {
    setExporting(type);
    try {
      const res = await api.get(`/reports/manager-remarks/export/${type}`, { params: params(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tamanna_sales_manager_reporting.${type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} exported`);
    } catch {
      toast.error(`Failed to export ${type.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="manager-reporting-panel">
      <div className="px-5 py-4 border-b border-slate-100 space-y-4">
        <div>
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[#1E3A8A]" /> Sales Manager Reporting
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">All remarks added by the Sales Manager on leads, filtered by remark date</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="mgr-report-from" />
          </div>
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="mgr-report-to" />
          </div>
          <Button onClick={load} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="mgr-report-run">
            <Search className="h-4 w-4 mr-1.5" /> Run
          </Button>
          <Button variant="outline" onClick={() => download("csv")} disabled={exporting === "csv"} data-testid="mgr-report-csv">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} CSV
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={exporting === "pdf"} data-testid="mgr-report-pdf">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} PDF
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400" data-testid="mgr-report-empty">No Sales Manager remarks for this range</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="mgr-report-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Remark Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Event Date</TableHead>
                <TableHead>Executive</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Remark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid="mgr-report-row">
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                  <TableCell>
                    <p className="font-medium text-slate-900">{r.customer_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{r.contact_no}</p>
                  </TableCell>
                  <TableCell className="text-slate-600 whitespace-nowrap">{r.event_date}</TableCell>
                  <TableCell className="text-slate-600">{r.executive}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-md whitespace-pre-wrap">
                    {r.text}
                    <p className="text-[11px] text-slate-400 mt-0.5">— {r.author_name}</p>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
